/**
 * Rate Limit Progress Bar Component
 * Visual representation of remaining messages with color transitions
 * Following soft, modern design with smooth animations
 */

import React from 'react';
import { motion } from 'motion/react';
import { cn } from '../../../../lib/utils';
import type { RateLimitState } from '../hooks/useChatRateLimit';

interface RateLimitProgressBarProps {
  rateLimitState: RateLimitState | null;
  className?: string;
  showLabel?: boolean;
  height?: 'sm' | 'md' | 'lg';
  showBurstOverlay?: boolean;
}

/**
 * Get color classes for progress bar
 */
const getProgressColor = (color: string, isRateLimited: boolean) => {
  if (isRateLimited) {
    return 'bg-red-500';
  }
  
  switch (color) {
    case 'green':
      return 'bg-emerald-500';
    case 'yellow':
      return 'bg-amber-500';
    case 'orange':
      return 'bg-orange-500';
    default:
      return 'bg-gray-500';
  }
};

/**
 * Get height classes
 */
const getHeightClasses = (height: 'sm' | 'md' | 'lg') => {
  switch (height) {
    case 'sm':
      return 'h-1';
    case 'lg':
      return 'h-2.5';
    default: // md
      return 'h-1.5';
  }
};

/**
 * Rate Limit Progress Bar Component
 * Shows visual progress of remaining messages
 */
export const RateLimitProgressBar: React.FC<RateLimitProgressBarProps> = ({
  rateLimitState,
  className,
  showLabel = false,
  height = 'md',
  showBurstOverlay = false,
}) => {
  if (!rateLimitState) {
    return null;
  }

  const { remaining, limit, percentage, color, isRateLimited, burstRemaining } = rateLimitState;
  const progressColor = getProgressColor(color, isRateLimited);
  const heightClass = getHeightClasses(height);
  const burstPercentage = limit > 0 ? (burstRemaining / limit) * 100 : 0;

  return (
    <div className={cn('w-full', className)}>
      {showLabel && (
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
            Messages Remaining
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-500 font-medium tabular-nums">
            {remaining}/{limit}
          </span>
        </div>
      )}
      
      <div className={cn('relative w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden', heightClass)}>
        {/* Main progress bar */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${Math.max(0, Math.min(100, percentage))}%` }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className={cn('h-full rounded-full transition-colors duration-300', progressColor)}
          role="progressbar"
          aria-valuenow={remaining}
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-label={`${remaining} of ${limit} messages remaining`}
        />
        
        {/* Burst limit overlay (if enabled) */}
        {showBurstOverlay && burstRemaining < remaining && (
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.max(0, Math.min(100, burstPercentage))}%` }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="absolute top-0 left-0 h-full bg-blue-400/40 rounded-full"
            style={{ mixBlendMode: 'multiply' }}
            aria-hidden="true"
          />
        )}
      </div>
      
      {/* Burst warning indicator */}
      {showBurstOverlay && burstRemaining < remaining && burstRemaining <= 2 && (
        <div className="mt-1 text-xs text-blue-600 dark:text-blue-400">
          Burst limit: {burstRemaining} remaining
        </div>
      )}
    </div>
  );
};

